import { showErrorToast } from "@configs/react-toastify/snackbar";
import useEscapeKey from "@hooks/useEscapeKey";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { OutlineButton } from "@components/Buttons";

const ComponentInformationModal = ({ show, onClose, componentData, "data-testid": dataTestId = "component-information-modal" }) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { t } = useTranslation();
  const { colors } = useTheme();

  // Use the custom hook for ESC key functionality
  useEscapeKey(onClose, show);

  const [componentDataSelected, setComponentDataSelected] = useState(null);

  const getComponentData = useMutationData(
    (data) => {
      if (!data?.componentName && !data?.component) return null;
      const componentToUse = data.component || data.componentName;
      if (!componentToUse || !data.componentVersion) return null;
      return `component_by_name_and_version?componentName=${encodeURIComponent(componentToUse)}&componentVersion=${encodeURIComponent(data.componentVersion)}`;
    },
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          const isEmpty =
            !response?.componentName &&
            !response?.componentVersion &&
            !response?.licenseName;
          setComponentDataSelected(isEmpty ? null : response);
        }
      },
      onError: (error) => {
        console.error("Error fetching component data:", error);
        showErrorToast(t("toast.loading-data-error"));
        setComponentDataSelected(null);
      },
    },
  );

  useEffect(() => {
    if (show && componentData) {
      getComponentData.mutate(componentData);
    } else {
      setComponentDataSelected(null);
    }
  }, [show, componentData]);

  if (!show) {
    return null;
  }
  const modalContent = (
    <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start md:justify-center p-4 backdrop-blur-[1px] overflow-x-auto`} data-testid={`${dataTestId}-overlay`}>
      <div className={`${colors.bgModal} w-[760px] min-w-[760px] max-h-[90vh] flex-shrink-0 overflow-auto rounded-lg shadow-xl xl:overflow-y-hidden flex flex-col`} data-testid={dataTestId}>
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0 border-b ${colors.borderPrimary}`} data-testid={`${dataTestId}-header`}>
          <h2 className="text-xl font-bold text-center text-white uppercase tracking-wide" data-testid={`${dataTestId}-title`}>
            {t("open-source.component-information-title")}
          </h2>
        </div>

        {/* Content */}
        <div className="px-6 py-4 pb-4 flex-1 min-h-0 overflow-y-auto" data-testid={`${dataTestId}-content`}>
          {getComponentData.isPending ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          ) : componentDataSelected ? (
            <div className="space-y-0">
              {/* Top Row: Component Details and License Details in 2 columns */}
              <div className={`border ${colors.borderPrimary} rounded-t-lg overflow-hidden`}>
                <div className="flex">
                  {/* Component Details Section - Left Column */}
                  <div className={`flex-1 border-r border-dashed ${colors.borderPrimary}`}>
                    <h4 className={`px-4 py-3 text-base uppercase font-semibold ${colors.textSecondary}`}>
                      {t("open-source.component-details")}
                    </h4>

                    {/* Component Name */}
                    <div className={`flex ${colors.borderPrimary}`} data-testid={fieldContainerTestId("component-name")}>
                      <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "8rem" }} data-testid={fieldLabelTestId("component-name")}>
                        {t("open-source.component-name")}
                      </div>
                      <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("component-name")}>
                        {componentDataSelected.componentName || componentDataSelected.component || "---"}
                      </div>
                    </div>

                    {/* Component Version */}
                    <div className={`flex ${colors.borderPrimary}`} data-testid={fieldContainerTestId("component-version")}>
                      <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "8rem" }} data-testid={fieldLabelTestId("component-version")}>
                        {t("open-source.component-version")}
                      </div>
                      <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("component-version")}>
                        {componentDataSelected.componentVersion || "---"}
                      </div>
                    </div>

                    {/* CPE */}
                    <div className={`flex`} data-testid={fieldContainerTestId("cpe")}>
                      <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "8rem" }} data-testid={fieldLabelTestId("cpe")}>
                        {t("open-source.cpe")}
                      </div>
                      <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("cpe")}>
                        {componentDataSelected.cpe || "---"}
                      </div>
                    </div>
                  </div>

                  {/* License Details Section - Right Column */}
                  <div className="flex-1">
                    <h4 className={`px-4 py-3 text-base uppercase font-semibold ${colors.textSecondary}`}>
                      {t("open-source.license-details")}
                    </h4>

                    {/* License Identifier */}
                    <div className={`flex ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-identifier")}>
                      <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "8rem" }} data-testid={fieldLabelTestId("license-identifier")}>
                        {t("open-source.license-identifier")}
                      </div>
                      <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("license-identifier")}>
                        {componentDataSelected.licenseIdentifier || componentDataSelected.componentLicense || "---"}
                      </div>
                    </div>

                    {/* License Name */}
                    <div className={`flex ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-name")}>
                      <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "8rem" }} data-testid={fieldLabelTestId("license-name")}>
                        {t("open-source.license-name")}
                      </div>
                      <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("license-name")}>
                        {componentDataSelected.licenseName || "---"}
                      </div>
                    </div>

                    {/* License Type */}
                    <div className={`flex`} data-testid={fieldContainerTestId("license-type")}>
                      <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "8rem" }} data-testid={fieldLabelTestId("license-type")}>
                        {t("open-source.license-type")}
                      </div>
                      <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("license-type")}>
                        {componentDataSelected.licenseTypeCdName || "---"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Links Section */}
              <div className={`border ${colors.borderPrimary} border-t-0 overflow-hidden`}>
                <h4 className={`px-4 py-3 text-base uppercase font-semibold ${colors.textSecondary}`}>
                  {t("open-source.links")}
                </h4>

                {/* Homepage URL */}
                <div className={`flex border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("homepage-url")}>
                  <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "10rem" }} data-testid={fieldLabelTestId("homepage-url")}>
                    {t("open-source.homepage-url")}
                  </div>
                  <div className={`flex-1 px-4 py-4 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("homepage-url")}>
                    {componentDataSelected.homepageUrl ? (
                      <a
                        href={componentDataSelected.homepageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${colors.textComparisonLink} ${colors.hoverTextComparisonLink} underline`}
                      >
                        {componentDataSelected.homepageUrl}
                      </a>
                    ) : (
                      "---"
                    )}
                  </div>
                </div>

                {/* Download URL */}
                <div className={`flex`} data-testid={fieldContainerTestId("download-url")}>
                  <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "10rem" }} data-testid={fieldLabelTestId("download-url")}>
                    {t("open-source.download-url")}
                  </div>
                  <div className={`flex-1 px-4 py-4 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("download-url")}>
                    {componentDataSelected.downloadUrl ? (
                      <a
                        href={componentDataSelected.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${colors.textComparisonLink} ${colors.hoverTextComparisonLink} underline`}
                      >
                        {componentDataSelected.downloadUrl}
                      </a>
                    ) : (
                      "---"
                    )}
                  </div>
                </div>
              </div>

              {/* Descriptive Notes Section */}
              <div className={`border ${colors.borderPrimary} border-t-0 rounded-b-lg overflow-hidden`}>
                <h4 className={`px-4 py-3 text-base uppercase font-semibold ${colors.textSecondary}`}>
                  {t("open-source.descriptive-notes")}
                </h4>

                {/* Copyright */}
                <div className={`flex border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("copyright")}>
                  <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "10rem" }} data-testid={fieldLabelTestId("copyright")}>
                    {t("open-source.copyright")}
                  </div>
                  <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("copyright")}>
                    {componentDataSelected.copyright || "---"}
                  </div>
                </div>

                {/* Description */}
                <div className={`flex border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("description")}>
                  <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "10rem" }} data-testid={fieldLabelTestId("description")}>
                    {t("open-source.description-short")}
                  </div>
                  <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("description")}>
                    {componentDataSelected.description ? (
                      <div className={`overflow-y-auto`} style={{ height: "6rem" }}>
                        {componentDataSelected.description}
                      </div>
                    ) : (
                      "---"
                    )}
                  </div>
                </div>

                {/* Remark */}
                <div className={`flex`} data-testid={fieldContainerTestId("remark")}>
                  <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "10rem" }} data-testid={fieldLabelTestId("remark")}>
                    {t("open-source.remark")}
                  </div>
                  <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("remark")}>
                    {componentDataSelected.remark || componentDataSelected.matchTypeRemark || "---"}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={`text-center py-8 text-white/80`}>{t("common.no-data-to-display")}</div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t ${colors.headerDividerBorder} flex justify-center flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
          <OutlineButton data-testid={`${dataTestId}-close-button`} onClick={onClose}>{t("common.cancel-button")}</OutlineButton>
        </div>
      </div>
    </div>
  );
  return createPortal(modalContent, document.body);
};

export default ComponentInformationModal;
